import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  FormDialogMode,
  SortDirection,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Card, CardContent, Typography } from '@material-ui/core'
import { Delete, Edit, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumEstadoCliente } from '../../../api'
import {
  NewSettlementGroupContractModel,
  PatchSettlementGroupContractModel,
  SettlementGroupContractModel
} from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import { ContractModel } from '../../../api/Argentia/model/contracts'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { CardEmptyContent } from '../../../components/shared/cardEmptyContent/cardEmptyContent'
import { LoadingCard } from '../../../components/shared/loadingCard/loadingCard'
import { enumKeyToValue, enumToSelectOptionArray } from '../../../helpers'
import { getUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../redux'
import { fetchSubjects } from '../../../services/subjects.service'
import { SubjectModel } from '../../clients/clients.model'
import { fetchContractsBySubjectID } from '../../contracts/contracts.service'
import { mapContractsToSelectOptions } from '../../procedures/procedures.mapper'
import { mapRowToMoveSettlementGroupContractRequest } from '../settlementGroups.mapper'
import { EnumSettlementGroupMoveTo, SetllementGroupContractRow } from '../settlementGroups.model'
import {
  createSettlementGroupContract,
  deleteSettlementGroupContract,
  fetchContractsBySgId,
  moveSettlementGroupContract,
  patchSettlementGroupContract
} from '../settlementGroups.service'

type FormAddContractData = {
  subjectId: number
  subject: string
  contracts: number[]
  vinculationStartDate: Date | undefined
  vinculationEndDate?: Date
  percentage?: number | null
}

type SettlementGroupContractsPageProps = {
  location: {
    state: {
      settlementGroupId: number
      settlementGroupCode: string
      settlementGroupSubject: SubjectModel | undefined
      flag: EnumTypeSettlementGroup
    }
  }
}

export const SettlementGroupContractsPage: React.FC<SettlementGroupContractsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'settlementGroups', 'enums'])
  const history = useHistory()

  const [contractData, setContractData] = useState<SetllementGroupContractRow[]>([])
  const [subjectContracts, setSubjectContracts] = useState<ContractModel[]>([])
  const [filteredSubjectContracts, setFilteredSubjectContracts] = useState<ContractModel[]>([])
  const [tableOptionsVar, setTableOptionsVar] = useState<DataTableOptions>()
  // const [fromFlexibilities, setFromFlexibilities] = useState<boolean>(
  //   props.location.state?.flag === EnumTypeSettlementGroup.FlexibilidadesGrupales ? true : false
  // )
  // const [fromBilling, setFromBilling] = useState<boolean>(
  //   props.location.state?.flag === EnumTypeSettlementGroup.FacturacionAgregada ? true : false
  // )
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false)

  const fromFlexibilities =
    props.location.state?.flag === EnumTypeSettlementGroup.FlexibilidadesGrupales
  const fromBilling = props.location.state?.flag === EnumTypeSettlementGroup.FacturacionAgregada
  const fromVirtualBattery = props.location.state?.flag === EnumTypeSettlementGroup.BateriaVirtual
  const fromBusinessGroup = props.location.state?.flag === EnumTypeSettlementGroup.GrupoEmpresarial

  const [selectAllContractsEnabled, setSelectAllContractsEnabled] = useState<boolean>(
    fromBilling || fromVirtualBattery
  ) // disable selectAllContracts until contracts are fetched. fromBilling and fromVirtualBattery has readonly-subject so must be enabled.

  const {
    openForm,
    updateFormDefinition,
    getFormData,
    updateFormData,
    closeForm,
    formProps,
    CustomFormDialog,
    setFormWaiting
  } = useFormDialog()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const groupId = props.location.state.settlementGroupId
  const groupCode = props.location.state.settlementGroupCode
  const subjectId = props.location.state?.settlementGroupSubject?.id ?? 0
  const subjectDesc = props.location.state?.settlementGroupSubject?.description ?? ''
  const flag = props.location.state?.flag

  const INITIAL_CONTRACT_FORM_ADD = {
    subjectId: subjectId,
    subject: subjectDesc,
    contracts: [],
    vinculationStartDate: undefined,
    percentage: null,
    selectAllContracts: false
  }

  const [formAddContractData, setFormAddContractData] =
    useState<FormAddContractData>(INITIAL_CONTRACT_FORM_ADD)
  const [formEditContractData, setFormEditContractData] = useState<any>()

  // Handlers Starts
  const handleClickBackButton = () => {
    history.goBack()
  }

  const handleAddDialogOpen = async () => {
    openForm({
      definition: formAddDefinition,
      title: t('settlementGroups:components.contracts.forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      inputData: formAddContractData,
      onClose: onCloseAddHandler
    })

    // Clean FormAddData
    setFormAddContractData(INITIAL_CONTRACT_FORM_ADD)
    return true
  }

  const onCloseAddHandler = async formData => {
    if (formData) {
      if (validateDates(formData, 'ADD') && validatePercentage(formData)) {
        // Map data from CustomFormDialog (Modal)
        let request: NewSettlementGroupContractModel[] = formData.contracts.map(contractId => {
          return {
            contractId: contractId,
            startDate: formData.vinculationStartDate,
            ...(formData.vinculationEndDate && { endDate: formData.vinculationEndDate }),
            ...(formData.percentage && { percentage: formData.percentage / 100 })
          } as NewSettlementGroupContractModel
        })
        if (fromVirtualBattery) {
          request = addOrderProperty(request)
        }
        // POST form Data
        await createSettlementGroupContract(groupId, request).then(response => {
          if (response.success) {
            info(t('settlementGroups:components.contracts.forms.add.notifications.save'))
            getContractsBySgId(groupId)
          } else {
            error(t('settlementGroups:components.contracts.forms.add.notifications.error'))
          }
        })
      } else {
        return
      }
    }
    closeForm()
  }

  const handleEditDialogOpen = async selectedRow => {
    setFormEditContractData({
      ...selectedRow,
      subject: subjectDesc
    })
    openForm({
      definition: formEditDefinition,
      title: t('settlementGroups:components.contracts.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        ...selectedRow,
        subject: subjectDesc
      },
      onClose: onCloseEditHandler
    })

    return true
  }

  const onCloseEditHandler = async formData => {
    if (formData) {
      if (validateDates(formData, 'EDIT') && validatePercentage(formData)) {
        // Map data from CustomFormDialog (Modal)
        const request: PatchSettlementGroupContractModel = {
          id: formData.id,
          contractId: formData.contractId,
          startDate: formData.vinculationStartDate,
          endDate: formData.vinculationEndDate ? formData.vinculationEndDate : null,
          ...(formData.percentage && { percentage: formData.percentage / 100 })
        }

        // PATCH form Data
        await patchSettlementGroupContract(groupId, formData.id, request).then(response => {
          if (response.success) {
            info(t('settlementGroups:components.contracts.forms.edit.notifications.save'))
            getContractsBySgId(groupId)
          } else {
            error(t('settlementGroups:components.contracts.forms.edit.notifications.error'))
          }
        })
      } else {
        return
      }
    }
    closeForm()
  }

  const handleDeleteDialogOpen = selectedRow => {
    getConfirmation(ConfirmationDialogs.DELETE_SETTLEMENTGROUP_CONTRACT).then(async () => {
      await deleteSettlementGroupContract(groupId, selectedRow.id).then(response => {
        if (response.success) {
          info(t('settlementGroups:components.contracts.notifications.delete.success'))
        } else {
          error(t('settlementGroups:components.contracts.notifications.delete.error'))
        }
        getContractsBySgId(groupId)
      })
      closeConfirmation()
    })
  }

  const handleContractMove = async (
    row: SetllementGroupContractRow,
    settlementGroupId: number,
    moveTo: EnumSettlementGroupMoveTo
  ) => {
    const request = mapRowToMoveSettlementGroupContractRequest(row, settlementGroupId, moveTo)
    await moveSettlementGroupContract(request).then(response => {
      if (response.success) {
        info(t('settlementGroups:components.contracts.notifications.move.success'))
        getContractsBySgId(groupId)
      } else {
        error(t('settlementGroups:components.contracts.notifications.move.error'))
      }
    })
  }
  // Handlers Ends

  // Functions Starts
  const mapToTableColumns = (
    data: SettlementGroupContractModel[]
  ): SetllementGroupContractRow[] => {
    const maxOrderNumber = getMaxOrderNumber(data)

    return data.map(d => ({
      id: d.id,
      contractId: d.contract.id,
      contractCode: d.contract.contractCode,
      description: d.contract.description,
      contractStartDate: moment(d.contract.startDate).toDate(),
      contractEndDate: moment(d.contract.endDate).toDate(),
      status: enumKeyToValue(EnumEstadoCliente, d.contract.status),
      vinculationStartDate: moment(d.startDate).toDate(),
      vinculationEndDate: d.endDate != null ? moment(d.endDate).toDate() : null,
      percentage: d.percentage ? d.percentage * 100 : null,
      order: d.order,
      isNotFirstOne: d.order !== undefined && d.order !== 1,
      isNotLastOne: d.order !== undefined && d.order !== maxOrderNumber
    }))
  }

  const getContractsBySgId = async (settlementGroupId: number) => {
    setIsFetching(true)
    await fetchContractsBySgId(settlementGroupId).then(response => {
      if (response.success && response.data != undefined) {
        setContractData(mapToTableColumns(response.data))
        setIsFetching(false)
      }
    })
  }

  const fetchContracts = async (query: number) => {
    //set contracts to loading state
    setIsLoadingContracts(true)

    await fetchContractsBySubjectID(query).then(response => {
      if (response.success) {
        if (response.data) {
          setSubjectContracts(response.data)
        }
      } else {
        error(t('settlementGroups:components.contracts.notifications.fetchContractsError'))
      }
      setIsLoadingContracts(false)
    })
  }

  const validateDates = (formData, formType: string): boolean => {
    const endDateBeforeStartDate = formData.vinculationEndDate
      ? moment(formData.vinculationEndDate).isBefore(formData.vinculationStartDate, 'day')
      : null

    const startDateNotWithinContractDate = !moment(formData.vinculationStartDate).isBetween(
      formData.contractStartDate,
      formData.contractEndDate,
      'day',
      '[]'
    )
    const endDateNotWithinContractDate = formData.vinculationEndDate
      ? !moment(formData.vinculationEndDate).isBetween(
          formData.contractStartDate,
          formData.contractEndDate,
          'day',
          '[]'
        )
      : null

    if (startDateNotWithinContractDate && formType === 'EDIT') {
      // excluding ADD form because multiselect option makes no-sense to select which contract date period to evaluate
      error(
        t(
          'settlementGroups:components.contracts.forms.edit.validations.startDateNotWithinContractDates'
        )
      )
      return false
    } else if (endDateNotWithinContractDate && formType === 'EDIT') {
      // excluding ADD form because multiselect option makes no-sense to select which contract date period to evaluate
      error(
        t(
          'settlementGroups:components.contracts.forms.edit.validations.endDateNotWithinContractDates'
        )
      )
      return false
    } else if (endDateBeforeStartDate && (formType === 'ADD' || formType === 'EDIT')) {
      error(t('settlementGroups:components.contracts.forms.edit.validations.beforeEndDate'))
      return false
    } else {
      return true
    }
  }

  const validatePercentage = (formData): boolean => {
    if (formData.percentage === null) {
      //handle optional field
      return true
    } else {
      const percentageIsValid: boolean = formData.percentage >= 0 && formData.percentage <= 100
      if (percentageIsValid) {
        return true
      } else {
        error(t('settlementGroups:components.contracts.forms.edit.validations.percentage'))
        return false
      }
    }
  }

  const handlePermissions = () => {
    if (fromFlexibilities) return hasPermission(Resources.Flexibility, [UserPermission.EDIT])
    else if (fromBilling) return hasPermission(Resources.GroupedBilling, [UserPermission.EDIT])
    else return hasPermission(Resources.VirtualBattery, [UserPermission.EDIT])
  }

  const getOrderNumber = (tableData: SetllementGroupContractRow[]): number | undefined => {
    const maxNumber = getMaxOrderNumber(tableData)
    return maxNumber && maxNumber + 1
  }

  const getMaxOrderNumber = (
    data: SettlementGroupContractModel[] | SetllementGroupContractRow[]
  ) => {
    return data && data.length > 0
      ? data.reduce((max, element) => ((element.order ?? 0) > (max.order ?? 0) ? element : max))
          ?.order
      : 0
  }

  const addOrderProperty = (request: NewSettlementGroupContractModel[]) => {
    const sortedRequest = request.sort((a, b) => a.contractId - b.contractId)
    const lastestOrderNumber = getOrderNumber(contractData) ?? 1
    const newRequest = sortedRequest.map((contract, index) => {
      return {
        ...contract,
        order: lastestOrderNumber + index
      } as NewSettlementGroupContractModel
    })
    return newRequest
  }
  // Functions Ends

  // Form Definition Starts
  const formAddDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD',
        sections: [
          {
            inputs: [
              {
                name: 'subject',
                label: t('settlementGroups:components.contracts.forms.add.inputs.subject'),
                type: DataType.TEXT,
                readOnly: true,
                display: fromBilling || fromVirtualBattery
              },
              {
                name: 'subjectFromFlexibilities',
                label: t('settlementGroups:components.contracts.forms.add.inputs.subject'),
                type: DataType.SEARCHFIELD,
                placeholder: t('settlementGroups:forms.add.placeholders.subject'),
                clearable: true,
                apiCall: (query: string) => {
                  setSelectAllContractsEnabled(false) // disable selectAllContracts until contracts are fetched.
                  return fetchSubjects(query)
                },
                onChange: (subjectSelected: any, formState) => {
                  //clean previous contracts from FormData
                  updateFormData({
                    ...formState,
                    contracts: [],
                    selectAllContracts: false
                  })
                  //update form MULTISELECT values
                  setSelectAllContractsEnabled(false)
                  subjectSelected &&
                    fetchContracts(subjectSelected.value).finally(() => {
                      setSelectAllContractsEnabled(true)
                    })
                },
                required: true,
                display: fromFlexibilities || fromBusinessGroup
              },
              {
                name: 'selectAllContracts',
                label: t(
                  'settlementGroups:components.contracts.forms.add.inputs.selectAllContracts'
                ),
                type: DataType.BOOLEAN,
                readOnly: !selectAllContractsEnabled,
                onChange: (value: string | number, formState) => {
                  if (+value) {
                    const allContracts = filteredSubjectContracts.map(contract => contract.id)
                    updateFormData({
                      ...formState,
                      contracts: allContracts
                    })
                  } else {
                    updateFormData({
                      ...formState,
                      contracts: []
                    })
                  }
                }
              },
              {
                name: 'contracts',
                label: t('settlementGroups:components.contracts.forms.add.inputs.contract'),
                type: DataType.MULTISELECT,
                isLoading: isLoadingContracts,
                selectOptions: mapContractsToSelectOptions(filteredSubjectContracts),
                required: true
              },
              {
                name: 'vinculationStartDate',
                label: t(
                  'settlementGroups:components.contracts.forms.add.inputs.vinculationStartDate'
                ),
                type: DataType.DATE,
                required: true
              },
              {
                name: 'vinculationEndDate',
                label: t(
                  'settlementGroups:components.contracts.forms.add.inputs.vinculationEndDate'
                ),
                type: DataType.DATE,
                clearable: true
              },
              {
                name: 'percentage',
                label: t('settlementGroups:components.contracts.forms.add.inputs.percentage'),
                type: DataType.NUMBER,
                decimalScale: DecimalScale.DEFAULT,
                helpText: t('settlementGroups:components.contracts.forms.add.helpText.percentage'),
                suffix: '%',
                clearable: true,
                display: fromFlexibilities
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isLoadingContracts,
      selectAllContractsEnabled,
      fromBilling,
      fromVirtualBattery,
      fromFlexibilities,
      fromBusinessGroup,
      filteredSubjectContracts
    ])

  const formEditDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'subject',
                label: t('settlementGroups:components.contracts.forms.edit.inputs.subject'),
                type: DataType.TEXT,
                readOnly: true,
                display: fromBilling || fromVirtualBattery
              },
              {
                name: 'contractCode',
                label: t('settlementGroups:components.contracts.forms.edit.inputs.contract'),
                type: DataType.TEXT,
                inputSize: 12,
                readOnly: true
              },
              {
                name: 'contractStartDate',
                label: t(
                  'settlementGroups:components.contracts.forms.edit.inputs.contractStartDate'
                ),
                type: DataType.DATE,
                readOnly: true
              },
              {
                name: 'contractEndDate',
                label: t('settlementGroups:components.contracts.forms.edit.inputs.contractEndDate'),
                type: DataType.DATE,
                readOnly: true
              },
              {
                name: 'vinculationStartDate',
                label: t(
                  'settlementGroups:components.contracts.forms.edit.inputs.vinculationStartDate'
                ),
                type: DataType.DATE,
                required: true
              },
              {
                name: 'vinculationEndDate',
                label: t(
                  'settlementGroups:components.contracts.forms.edit.inputs.vinculationEndDate'
                ),
                type: DataType.DATE
              },
              {
                name: 'percentage',
                label: t('settlementGroups:components.contracts.forms.edit.inputs.percentage'),
                type: DataType.NUMBER,
                decimalScale: DecimalScale.DEFAULT,
                helpText: t('settlementGroups:components.contracts.forms.add.helpText.percentage'),
                suffix: '%',
                clearable: true,
                display: fromFlexibilities
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromFlexibilities, fromBilling, fromVirtualBattery])

  // Form Definition Ends

  // DataTable Config Starts
  const dialogTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      type: DataType.NUMBER,
      display: false
    },
    {
      name: 'contractCode',
      label: t('settlementGroups:components.contracts.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('settlementGroups:components.contracts.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'contractStartDate',
      label: t('settlementGroups:components.contracts.dataTable.columns.contractStartDate'),
      type: DataType.DATE
    },
    {
      name: 'contractEndDate',
      label: t('settlementGroups:components.contracts.dataTable.columns.contractEndDate'),
      type: DataType.DATE
    },
    {
      name: 'status',
      label: t('settlementGroups:components.contracts.dataTable.columns.status'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoCliente, 'enums:EnumEstadoCliente')
    },
    {
      name: 'vinculationStartDate',
      label: t('settlementGroups:components.contracts.dataTable.columns.vinculationStartDate'),
      type: DataType.DATE
    },
    {
      name: 'vinculationEndDate',
      label: t('settlementGroups:components.contracts.dataTable.columns.vinculationEndDate'),
      type: DataType.DATE
    },
    {
      name: 'percentage',
      label: t('settlementGroups:components.contracts.dataTable.columns.percentage'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.DEFAULT,
      display: fromFlexibilities
    },
    {
      name: 'order',
      label: t('settlementGroups:components.contracts.dataTable.columns.order'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      display: fromVirtualBattery,
      sortable: false
      // columnWidth: 250
    }
  ]

  let dialogTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:components.contracts.dataTable.analytics')
    },
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    ...(fromVirtualBattery && { sortOrder: { name: 'order', direction: SortDirection.ASC } }),
    // ...(fromFlexibilities && { customInsertEditModal: true }),
    customInsertEditModal: handlePermissions(),
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    onRowInsert: handleAddDialogOpen,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('settlementGroups:components.contracts.dataTable.rowCustomActions.edit'),
        onClick: handleEditDialogOpen,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('settlementGroups:components.contracts.dataTable.rowCustomActions.delete'),
        onClick: handleDeleteDialogOpen,
        userPermission: UserPermission.EDIT
      }
    ]
  }

  if (fromVirtualBattery) {
    dialogTableOptions.rowCustomActions?.push(
      {
        icon: <KeyboardArrowDown fontSize="small" />,
        tooltip: t('settlementGroups:components.contracts.dataTable.rowCustomActions.down'),
        onClick: (row: SetllementGroupContractRow) => {
          handleContractMove(row, groupId, EnumSettlementGroupMoveTo.ORDER_DOWN)
        },
        visibleIf: 'isNotLastOne',
        userPermission: UserPermission.EDIT
      },
      {
        icon: <KeyboardArrowUp fontSize="small" />,
        tooltip: t('settlementGroups:components.contracts.dataTable.rowCustomActions.up'),
        onClick: (row: SetllementGroupContractRow) => {
          handleContractMove(row, groupId, EnumSettlementGroupMoveTo.ORDER_UP)
        },
        visibleIf: 'isNotFirstOne',
        userPermission: UserPermission.EDIT
      }
    )
  }

  // DataTable Config Ends

  // Hooks Starts

  // useEffect exclusive for sideEffects
  useEffect(() => {
    getContractsBySgId(groupId)
    fetchContracts(subjectId)
  }, [])

  // useEffect for updating Modal Form (AddForm)
  useEffect(() => {
    // get used contracts
    const usedContracts = contractData.map(contract => contract.contractId)
    // filter unused contracts
    const unusedContracts = subjectContracts.filter(
      responseContract => !usedContracts.includes(responseContract.id)
    )
    // update SelectOption
    setFilteredSubjectContracts(unusedContracts)
  }, [contractData, subjectContracts])

  // useEffect for updating Modal Form (EditForm) (customHook needed)
  useEffect(() => {
    updateFormDefinition(formAddDefinition)
    updateFormDefinition(formEditDefinition)
  }, [formEditDefinition, formAddDefinition])
  // }, [formEditContractData])

  // Hooks Ends

  if (!groupCode || !flag) {
    history.push(NonAuthRoutes.SettlementGroups)
  }

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('settlementGroups:components.contracts.title', { groupCode })}>
        <Button variant="text" color="primary" onClick={handleClickBackButton}>
          {t('settlementGroups:actions.back')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            <BreadcrumbLink toPathName="/settlementGroups">
              {`${t('settlementGroups:breadcrumbs.settlementGroups')}`}
            </BreadcrumbLink>
            <Typography key="3" color="primary" variant="subtitle2">
              {t('settlementGroups:breadcrumbs.group', { groupCode })}
            </Typography>
            <Typography key="3" color="primary" variant="subtitle1">
              {t('settlementGroups:breadcrumbs.contracts')}
            </Typography>
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <LoadingCard />
        ) : contractData.length === 0 ? (
          <CardEmptyContent
            title={t('settlementGroups:components.contracts.emptyCard.title')}
            caption={t('settlementGroups:components.contracts.emptyCard.caption')}
          >
            <Button size="small" color="primary" variant="contained" onClick={handleAddDialogOpen}>
              {t('settlementGroups:components.contracts.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        ) : (
          <Card raised>
            <CardContent>
              <DataTable
                title={t('settlementGroups:components.contracts.dataTable.title')}
                data={contractData}
                columns={dialogTableColumns}
                options={dialogTableOptions}
                userPermissions={getUserPermissions(
                  fromFlexibilities ? Pages.Flexibility : Pages.SettlementGroupAggregatedAdd
                )}
              />
            </CardContent>
          </Card>
        )}
        <CustomFormDialog {...formProps} />
      </PageCenterColumn>
    </Page>
  )
}
